import React, { useEffect, useMemo, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { SPORTS } from "../../constants/vars";
import { toast } from "react-toastify";
import { GlobalStyles } from "../../constants/styles";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const CREATE_SPONSOR = gql`
    mutation CreateSponsor(
        $name: String!
        $contactName: String!
        $contactEmail: String!
        $website: String
        $primarySport: String!
        $notes: String
        $isActive: Boolean!
        $billingCountryCode: String
        $billingCurrency: String
    ) {
        createSponsor(
            name: $name
            contactName: $contactName
            contactEmail: $contactEmail
            website: $website
            primarySport: $primarySport
            notes: $notes
            isActive: $isActive
            billingCountryCode: $billingCountryCode
            billingCurrency: $billingCurrency
        ) {
            ok
            error
            sponsor {
                id
                name
                contactName
                contactEmail
                website
                primarySport
                notes
                isActive
                billingCountryCode
                billingCurrency
            }
        }
    }
`;

const COUNTRY_CURRENCY_DEFAULTS = {
    US: "usd",
    CA: "cad",
};

const INITIAL_FORM = {
    name: "",
    contactName: "",
    contactEmail: "",
    website: "",
    primarySport: "",
    notes: "",
    isActive: true,
    billingCountryCode: "",
    billingCurrency: "",
};

export default function AddSponsorModal({ show, onClose, onCreated, zIndex = 1050 }) {
    const scopeId = "kbola-sponsor-add-modal";
    const { t } = useTranslation();

    const [form, setForm] = useState(INITIAL_FORM);

    const modalStyles = useMemo(() => {
        return {
            wrap: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
                background: "#fff",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: "linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.28), rgba(0,0,0,0))",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: { display: "flex", alignItems: "center", gap: 10 },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: GlobalStyles.colors.woodsmoke,
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.95)",
                border: "1px solid rgba(0,0,0,0.10)",
            },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: "#14151a",
            },
            closeBtn: {
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: { padding: 14, background: "#fff" },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "10px 12px",
                fontWeight: 800,
                outline: "none",
            },
            select: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "0px 12px",
                height: 44,
                fontWeight: 800,
                outline: "none",
                backgroundColor: "#fff",
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 14px",
                background: "rgba(0,0,0,0.02)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
            },
            btnGhost: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
        };
    }, []);

    const forceUnlockPage = () => {
        document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.body.style.removeProperty("overflow");
    };

    const safeClose = () => {
        forceUnlockPage();
        onClose?.();
        setTimeout(forceUnlockPage, 0);
        setTimeout(forceUnlockPage, 150);
    };

    useEffect(() => {
        if (show) {
            document.body.classList.add("modal-open");
            document.body.style.overflow = "hidden";
        } else {
            forceUnlockPage();
        }
        return () => forceUnlockPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const [createSponsor, { loading, error }] = useMutation(CREATE_SPONSOR, {
        onError: (err) => toast.error(getLocalizedGraphQLError(err, t, "sponsors.form.toast.createError")),
    });

    const canSubmit =
        !!form.name.trim() &&
        !!form.contactName.trim() &&
        !!form.contactEmail.trim() &&
        !!form.primarySport;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === "billingCountryCode") {
            const country = (value || "").toUpperCase();
            const suggestedCurrency = COUNTRY_CURRENCY_DEFAULTS[country] || "";
            setForm((f) => ({
                ...f,
                billingCountryCode: country,
                // only auto-set if currency empty or currently one of our defaults
                billingCurrency: !f.billingCurrency || f.billingCurrency === "usd" || f.billingCurrency === "cad"
                    ? suggestedCurrency
                    : f.billingCurrency,
            }));
            return;
        }

        if (name === "billingCurrency") {
            setForm((f) => ({ ...f, billingCurrency: (value || "").toLowerCase() }));
            return;
        }

        setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    };

    const normalizePayload = () => {
        const website = form.website.trim();
        return {
            name: form.name.trim(),
            contactName: form.contactName.trim(),
            contactEmail: form.contactEmail.trim().toLowerCase(),
            website: website || null,
            primarySport: form.primarySport,
            notes: form.notes.trim() || null,
            isActive: !!form.isActive,
            billingCountryCode: form.billingCountryCode ? form.billingCountryCode.toUpperCase() : null,
            billingCurrency: form.billingCurrency ? form.billingCurrency.toLowerCase() : null,
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit || loading) return;

        const payload = normalizePayload();
        const { data } = await createSponsor({ variables: payload });

        const result = data?.createSponsor;
        if (!result?.ok) {
            toast.error(result?.error || t("sponsors.form.toast.createCouldNot"));
            return;
        }

        if (result?.sponsor) {
            onCreated?.(result.sponsor);
            toast.success(t("sponsors.form.toast.created"));
            setForm(INITIAL_FORM);
            safeClose();
        }
    };

    if (!show) return null;

    return (
        <>
            <div
                id={scopeId}
                className="modal fade show d-block"
                style={{ zIndex }}
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                    <div className="modal-content" style={modalStyles.wrap}>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-header" style={modalStyles.header}>
                                <div style={modalStyles.titleWrap}>
                                    <span style={modalStyles.badge}>{t("sponsors.form.newBadge")}</span>
                                    <h4 className="modal-title" style={modalStyles.title}>
                                        {t("sponsors.form.newSponsor")}
                                    </h4>
                                </div>

                                <button
                                    type="button"
                                    onClick={safeClose}
                                    aria-label={t("sponsors.form.close")}
                                    style={modalStyles.closeBtn}
                                    title={t("sponsors.form.close")}
                                    disabled={loading}
                                >
                                    <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>
                                        &times;
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body" style={modalStyles.body}>
                                {error ? <div className="alert alert-danger">{getLocalizedGraphQLError(error, t, "errors.somethingWentWrong")}</div> : null}

                                <div style={modalStyles.card}>
                                    <div className="form-group" style={{ marginBottom: 12 }}>
                                        <label style={modalStyles.label}>{t("sponsors.form.sponsorBusinessName")} *</label>
                                        <input
                                            className="form-control"
                                            name="name"
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                            style={modalStyles.input}
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group col-md-6" style={{ marginBottom: 12 }}>
                                            <label style={modalStyles.label}>{t("sponsors.form.contactName")} *</label>
                                            <input
                                                className="form-control"
                                                name="contactName"
                                                value={form.contactName}
                                                onChange={handleChange}
                                                required
                                                style={modalStyles.input}
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="form-group col-md-6" style={{ marginBottom: 12 }}>
                                            <label style={modalStyles.label}>{t("sponsors.form.contactEmail")} *</label>
                                            <input
                                                className="form-control"
                                                type="email"
                                                name="contactEmail"
                                                value={form.contactEmail}
                                                onChange={handleChange}
                                                required
                                                style={modalStyles.input}
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group col-md-6" style={{ marginBottom: 12 }}>
                                            <label style={modalStyles.label}>{t("sponsors.form.websiteLandingUrl")}</label>
                                            <input
                                                className="form-control"
                                                name="website"
                                                value={form.website}
                                                onChange={handleChange}
                                                placeholder="https://example.com"
                                                style={modalStyles.input}
                                                disabled={loading}
                                            />
                                        </div>

                                        <div className="form-group col-md-6" style={{ marginBottom: 12 }}>
                                            <label style={modalStyles.label}>{t("sponsors.form.primarySport")} *</label>
                                            <select
                                                className="form-control"
                                                name="primarySport"
                                                value={form.primarySport}
                                                onChange={handleChange}
                                                required
                                                style={modalStyles.select}
                                                disabled={loading}
                                            >
                                                <option value="">{t("sponsors.form.selectSport")}</option>
                                                {SPORTS.map((s) => (
                                                    <option key={s.key} value={s.key}>
                                                        {t(`signUp.form.sports.${s.key}`, {}, s.value)}
                                                    </option>
                                                ))}
                                                <option value="multi">{t("sponsors.form.multipleGeneralSports")}</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group col-md-6" style={{ marginBottom: 12 }}>
                                            <label style={modalStyles.label}>{t("sponsors.form.billingCountryOptional")}</label>
                                            <select
                                                className="form-control"
                                                name="billingCountryCode"
                                                value={form.billingCountryCode}
                                                onChange={handleChange}
                                                style={modalStyles.select}
                                                disabled={loading}
                                            >
                                                <option value="">{t("sponsors.form.useProfileDefault")}</option>
                                                <option value="US">{t("sponsors.form.unitedStatesUs")}</option>
                                                <option value="CA">{t("sponsors.form.canadaCa")}</option>
                                            </select>
                                        </div>

                                        <div className="form-group col-md-6" style={{ marginBottom: 12 }}>
                                            <label style={modalStyles.label}>{t("sponsors.form.billingCurrencyOptional")}</label>
                                            <select
                                                className="form-control"
                                                name="billingCurrency"
                                                value={form.billingCurrency}
                                                onChange={handleChange}
                                                style={modalStyles.select}
                                                disabled={loading}
                                            >
                                                <option value="">{t("sponsors.form.useProfileCountryDefault")}</option>
                                                <option value="usd">USD</option>
                                                <option value="cad">CAD</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="form-group" style={{ marginBottom: 12 }}>
                                        <label style={modalStyles.label}>{t("sponsors.form.notesInternalOnly")}</label>
                                        <textarea
                                            className="form-control"
                                            name="notes"
                                            rows={3}
                                            value={form.notes}
                                            onChange={handleChange}
                                            placeholder={t("sponsors.form.notesPlaceholder")}
                                            style={modalStyles.input}
                                            disabled={loading}
                                        />
                                    </div>

                                    <div className="form-group form-check" style={{ marginBottom: 0 }}>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="isActive"
                                            name="isActive"
                                            checked={!!form.isActive}
                                            onChange={handleChange}
                                            disabled={loading}
                                        />
                                        <label className="form-check-label" htmlFor="isActive" style={{ fontWeight: 800 }}>
                                            {t("sponsors.form.activeSponsor")}
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer" style={modalStyles.footer}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={safeClose}
                                    style={{ ...modalStyles.btn, ...modalStyles.btnGhost, opacity: loading ? 0.7 : 1 }}
                                    disabled={loading}
                                >
                                    <i className="fas fa-times" />
                                    {t("sponsors.form.close")}
                                </button>

                                <button
                                    type="submit"
                                    className="btn"
                                    style={{ ...modalStyles.btn, ...modalStyles.btnPrimary, opacity: loading ? 0.8 : 1 }}
                                    disabled={loading || !canSubmit}
                                >
                                    <i className="fas fa-check" />
                                    {loading ? t("sponsors.form.saving") : t("sponsors.form.save")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <style>{`
        #${scopeId}.modal{
          font-family: 'Poppins', sans-serif;
          background: rgba(0, 0, 0, 0.45);
          backdrop-filter: blur(2px);
        }

        #${scopeId} .modal-dialog{
          max-height: 92vh;
          display: flex;
          flex-direction: column;
          margin-top: 1.25rem;
          margin-bottom: 1.25rem;
        }

        #${scopeId} .modal-content{
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        #${scopeId} .modal-body{
          overflow-y: auto;
          max-height: 70vh;
          -webkit-overflow-scrolling: touch;
        }

        #${scopeId} .form-control:focus{
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.9);
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
        }

        #${scopeId} i.fas,
        #${scopeId} i.far,
        #${scopeId} i.fal,
        #${scopeId} i.fa,
        #${scopeId} i[class^="fa-"],
        #${scopeId} i[class*=" fa-"]{
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }

        @media (max-width: 768px) {
          #${scopeId} .modal-dialog {
            max-width: 95%;
            margin: 0.9rem auto;
          }
          #${scopeId} .modal-body {
            max-height: 74vh;
          }
        }
      `}</style>
        </>
    );
}
